import React, { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { db, functions } from "../firebase-config";
import { httpsCallable } from "firebase/functions";
import html2pdf from "html2pdf.js";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  CircularProgress,
  TableSortLabel,
  TableContainer,
  Paper,
  useMediaQuery,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  TablePagination,
  IconButton,
  Stack,
  Box,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Typography,
  alpha,
  Badge,
  Tooltip,
  Avatar,
  Grid,
  Divider,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Eye,
  Trash2,
  Search,
  Filter,
  Download,
  Calendar,
  Store,
  User,
  PoundSterling,
  CheckCircle,
  XCircle,
  Clock,
  Truck,
  Package,
  MoreVertical,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Mail,
} from "lucide-react";
import InvoiceModal from "../components/invoice-modal";
import Header from "../components/header";
import { styled } from "@mui/material/styles";

const InvoiceScreen = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [orderStatusValue, setOrderStatusValue] = useState("");
  const [paymentStatusValue, setPaymentStatusValue] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [users, setUsers] = useState({});

  // Email send state
  const [emailDialog, setEmailDialog] = useState({ open: false, email: "", invoice: null });
  const [emailSending, setEmailSending] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    delivered: 0,
    totalRevenue: 0,
  });

  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");

  // Custom styled components
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:hover": {
      backgroundColor: theme.palette.primary.light + "20",
    },
    "&.selected": {
      backgroundColor: theme.palette.primary.light + "40",
    },
  }));

  const StatusChip = styled(Chip)(({ theme, status }) => {
    const statusColors = {
      pending: theme.palette.warning.light,
      accepted: theme.palette.primary.light,
      shipped: theme.palette.info?.light || "#E0F2FE",
      delivered: theme.palette.success.light,
    };
    const textColors = {
      pending: theme.palette.warning.dark,
      accepted: theme.palette.primary.dark,
      shipped: theme.palette.info?.dark || "#0369A1",
      delivered: theme.palette.success.dark,
    };

    return {
      backgroundColor: statusColors[status] || theme.palette.grey[200],
      color: textColors[status] || theme.palette.grey[700],
      fontWeight: 600,
      borderRadius: "6px",
      fontSize: "12px",
      padding: "2px 8px",
      height: "24px",
    };
  });

  const PaymentChip = styled(Chip)(({ theme, paid }) => ({
    backgroundColor: paid ? theme.palette.success.light : theme.palette.error.light,
    color: paid ? theme.palette.success.dark : theme.palette.error.dark,
    fontWeight: 600,
    borderRadius: "6px",
    fontSize: "12px",
    padding: "2px 8px",
    height: "24px",
  }));

  useEffect(() => {
    const q = query(collection(db, "invoices"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const invoiceData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInvoices(invoiceData);
      applyFilters(invoiceData, searchQuery, filterStatus, filterPayment, dateFrom, dateTo);
      calculateStats(invoiceData);
      setLoading(false);
    });

    // Real-time users fetch for latest restaurant/user info
    const usersRef = collection(db, "users");
    const unsubscribeUsers = onSnapshot(usersRef, (snapshot) => {
      const userData = {};
      snapshot.docs.forEach((doc) => {
        userData[doc.id] = { id: doc.id, ...doc.data() };
      });
      setUsers(userData);
    });

    return () => {
      unsubscribe();
      unsubscribeUsers();
    };
  }, []);

  useEffect(() => {
    if (!loading) {
      applyFilters(invoices, searchQuery, filterStatus, filterPayment, dateFrom, dateTo);
    }
  }, [invoices, users, searchQuery, filterStatus, filterPayment, dateFrom, dateTo, loading]);

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      pending: data.filter(inv => inv.orderStatus === "pending").length,
      delivered: data.filter(inv => inv.orderStatus === "delivered").length,
      totalRevenue: data.reduce((sum, inv) => sum + (inv.totalPrice || 0), 0),
    };
    setStats(stats);
  };

  const applyFilters = (data, search, status, payment, fromDate, toDate) => {
    let filtered = data;

    // Apply search filter
    if (search) {
      filtered = filtered.filter(invoice => {
        const liveUser = users[invoice.userId];
        const restaurantName = liveUser?.restaurantName || invoice.restaurantName || "";
        const customerName = liveUser?.name || invoice.name || "";

        return (
          restaurantName.toLowerCase().includes(search.toLowerCase()) ||
          customerName.toLowerCase().includes(search.toLowerCase()) ||
          invoice.id?.toLowerCase().includes(search.toLowerCase())
        );
      });
    }

    // Apply status filter
    if (status !== "all") {
      filtered = filtered.filter(invoice => invoice.orderStatus === status);
    }

    // Apply payment filter
    if (payment !== "all") {
      filtered = filtered.filter(invoice =>
        payment === "paid" ? invoice.isBillPaid : !invoice.isBillPaid
      );
    }

    // Apply date range filter
    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(invoice => {
        const invoiceDate = new Date(invoice.createdAt);
        return invoiceDate >= start;
      });
    }
    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(invoice => {
        const invoiceDate = new Date(invoice.createdAt);
        return invoiceDate <= end;
      });
    }

    setFilteredInvoices(filtered);
    setPage(0); // Reset to first page when filters change
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
  };

  const handlePaymentFilter = (payment) => {
    setFilterPayment(payment);
  };

  const handleDateFromChange = (value) => {
    setDateFrom(value);
    applyFilters(invoices, searchQuery, filterStatus, filterPayment, value, dateTo);
  };

  const handleDateToChange = (value) => {
    setDateTo(value);
    applyFilters(invoices, searchQuery, filterStatus, filterPayment, dateFrom, value);
  };

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sorted = [...filteredInvoices].sort((a, b) => {
      if (column === "totalPrice") {
        return direction === "asc"
          ? parseFloat(a[column]) - parseFloat(b[column])
          : parseFloat(b[column]) - parseFloat(a[column]);
      }

      if (column === "createdAt") {
        return direction === "asc"
          ? new Date(a[column]) - new Date(b[column])
          : new Date(b[column]) - new Date(a[column]);
      }

      if (a[column] < b[column]) return direction === "asc" ? -1 : 1;
      if (a[column] > b[column]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredInvoices(sorted);
    setSortConfig({ key: column, direction });
  };

  const handleOrderStatusChange = async (invoiceId, newStatus) => {
    try {
      const invoiceRef = doc(db, "invoices", invoiceId);
      await updateDoc(invoiceRef, { orderStatus: newStatus });
      setEditingInvoiceId(null);
      showSnackbar("Order status updated successfully", "success");
    } catch (error) {
      console.error("Error updating order status:", error);
      showSnackbar("Failed to update order status", "error");
    }
  };

  const handlePaymentStatusChange = async (invoiceId, isPaid) => {
    try {
      const invoiceRef = doc(db, "invoices", invoiceId);
      await updateDoc(invoiceRef, { isBillPaid: isPaid });
      setEditingInvoiceId(null);
      showSnackbar("Payment status updated successfully", "success");
    } catch (error) {
      console.error("Error updating payment status:", error);
      showSnackbar("Failed to update payment status", "error");
    }
  };

  const handleDelete = async () => {
    const updateSoldQuantity = async (item) => {
      const q = query(
        collection(db, "inventoryItems"),
        where("title", "==", item.title),
        where("brand", "==", item.brand)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        const inventoryItem = querySnapshot.docs[0].data();

        await updateDoc(docRef, {
          soldQuantity: Math.max(0, inventoryItem.soldQuantity - item.quantity),
        });
      }
    };

    try {
      if (deleteType === "single" && invoiceToDelete) {
        const invoiceDoc = await getDoc(doc(db, "invoices", invoiceToDelete));
        if (invoiceDoc.exists()) {
          const invoice = invoiceDoc.data();
          for (const item of invoice.items) {
            await updateSoldQuantity(item);
          }
        }

        await deleteDoc(doc(db, "invoices", invoiceToDelete));
        showSnackbar("Invoice deleted successfully", "success");
      } else if (deleteType === "multiple") {
        for (const id of selectedInvoices) {
          const invoiceDoc = await getDoc(doc(db, "invoices", id));
          if (invoiceDoc.exists()) {
            const invoice = invoiceDoc.data();
            for (const item of invoice.items) {
              await updateSoldQuantity(item);
            }
          }
          await deleteDoc(doc(db, "invoices", id));
        }
        showSnackbar(`${selectedInvoices.length} invoices deleted successfully`, "success");
      }

      setDeleteDialogOpen(false);
      setSelectedInvoices([]);
      setInvoiceToDelete(null);
    } catch (error) {
      console.error("Error deleting invoices:", error);
      showSnackbar("Failed to delete invoices", "error");
    }
  };

  const toggleSelectInvoice = (id) => {
    setSelectedInvoices((prev) =>
      prev.includes(id)
        ? prev.filter((invoiceId) => invoiceId !== id)
        : [...prev, id]
    );
  };

  const isAllSelected =
    filteredInvoices.length > 0 &&
    selectedInvoices.length === filteredInvoices.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(filteredInvoices.map((invoice) => invoice.id));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Email send handler
  const handleSendEmail = async () => {
    const { email, invoice } = emailDialog;
    if (!email || !invoice) return;

    setEmailSending(true);
    try {
      // Build a simple HTML summary for the email body
      const invoiceDate = new Date(invoice.createdAt).toLocaleDateString('en-GB');
      const totalAmount = (invoice.discountedTotal ?? invoice.totalPrice ?? 0).toFixed(2);
      const itemsHtml = (invoice.items || []).map(item => {
        const price = parseFloat(item.price) || 0;
        const total = price * (item.quantity || 1);
        return `<tr><td style="padding:8px;border-bottom:1px solid #eee">${item.title}</td><td style="padding:8px;text-align:center;border-bottom:1px solid #eee">${item.quantity}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee">£${price.toFixed(2)}</td><td style="padding:8px;text-align:right;border-bottom:1px solid #eee">£${total.toFixed(2)}</td></tr>`;
      }).join('');

      const htmlBody = `
        <div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
          <div style="background:linear-gradient(135deg,#C4161C,#F4B400);color:white;padding:20px;text-align:center;border-radius:10px 10px 0 0">
            <h1 style="margin:0">📋 Invoice</h1>
            <p style="margin:5px 0 0">Madras Flavours Kitchen Limited - ${invoiceDate}</p>
          </div>
          <div style="padding:20px;background:#f9f9f9;border:1px solid #ddd">
            <p><strong>Customer:</strong> ${invoice.name || 'N/A'}</p>
            <p><strong>Restaurant:</strong> ${invoice.restaurantName || 'N/A'}</p>
            <table style="width:100%;border-collapse:collapse;margin:15px 0">
              <thead><tr style="background:#f5f5f5"><th style="padding:8px;text-align:left">Item</th><th style="padding:8px;text-align:center">Qty</th><th style="padding:8px;text-align:right">Price</th><th style="padding:8px;text-align:right">Total</th></tr></thead>
              <tbody>${itemsHtml}</tbody>
            </table>
            <p style="text-align:right;font-size:18px"><strong>Total: £${totalAmount}</strong></p>
          </div>
          <div style="padding:15px;text-align:center;color:#666;font-size:12px;border-top:1px solid #ddd">
            <p>This email was sent from Madras Flavours Kitchen Limited.</p>
          </div>
        </div>
      `;

      // Generate PDF for attachment
      let pdfBase64 = null;
      try {
        const options = {
          margin: [0.5, 0.5, 0.5, 0.5],
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };
        const pdfDataUri = await html2pdf().from(htmlBody).set(options).outputPdf('datauristring');
        pdfBase64 = pdfDataUri.split(',')[1];
      } catch (pdfErr) {
        console.error("Error generating PDF for attachment:", pdfErr);
      }

      const sendEmail = httpsCallable(functions, 'manualSendInvoiceEmail');
      await sendEmail({
        recipientEmail: email,
        subject: `📧 Invoice - ${invoice.name || 'Customer'} - ${invoiceDate}`,
        htmlBody: htmlBody,
        pdfBase64: pdfBase64,
        fileName: `Invoice_${invoice.id.substring(0, 8)}.pdf`,
        restaurantId: invoice.userId || null,
        restaurantName: invoice.restaurantName || null,
        invoiceDate: invoiceDate,
      });

      showSnackbar(`📧 Invoice sent to ${email}`, 'success');
      setEmailDialog({ open: false, email: '', invoice: null });
    } catch (error) {
      console.error('Error sending invoice email:', error);
      showSnackbar(`❌ Failed to send email: ${error.message}`, 'error');
    } finally {
      setEmailSending(false);
    }
  };

  // Open email dialog for an invoice
  const handleOpenEmailDialog = (invoice) => {
    const currentUser = users[invoice.userId];
    const restaurantEmail = currentUser?.email || invoice.restaurantEmail || "";

    setEmailDialog({
      open: true,
      email: restaurantEmail,
      invoice: {
        ...invoice,
        restaurantName: currentUser?.restaurantName || invoice.restaurantName,
        name: currentUser?.name || invoice.name,
        address: currentUser?.address || invoice.address
      }
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getCurrentPageData = () => {
    return filteredInvoices.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock size={14} />;
      case "accepted": return <Package size={14} />;
      case "shipped": return <Truck size={14} />;
      case "delivered": return <CheckCircle size={14} />;
      default: return <Clock size={14} />;
    }
  };

  const getPaymentIcon = (paid) => {
    return paid ? <CheckCircle size={14} /> : <XCircle size={14} />;
  };

  return (
    <Box sx={{ backgroundColor: "#FAFAFA", minHeight: "100vh", p: { xs: 1, sm: 2, md: 3 } }}>
      <Header title="Orders" />

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3, mt: 0.1 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: "primary.light", width: 40, height: 40 }}>
                  <Package size={20} color="#C4161C" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Orders
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: "warning.light", width: 40, height: 40 }}>
                  <Clock size={20} color="#F59E0B" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {stats.pending}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: "success.light", width: 40, height: 40 }}>
                  <CheckCircle size={20} color="#16A34A" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {stats.delivered}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivered
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: "secondary.light", width: 40, height: 40 }}>
                  <PoundSterling size={20} color="#F4B400" />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    £{stats.totalRevenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 3,
          backgroundColor: "white",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by restaurant, name, or order ID..."
              value={searchQuery}
              onChange={handleSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                },
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Select
              fullWidth
              size="small"
              value={filterStatus}
              onChange={(e) => handleStatusFilter(e.target.value)}
              displayEmpty
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="shipped">Shipped</MenuItem>
              <MenuItem value="delivered">Delivered</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={6} md={2}>
            <Select
              fullWidth
              size="small"
              value={filterPayment}
              onChange={(e) => handlePaymentFilter(e.target.value)}
              displayEmpty
              sx={{ borderRadius: 3 }}
            >
              <MenuItem value="all">All Payments</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              <TextField
                type="date"
                size="small"
                label="From"
                value={dateFrom}
                onChange={(e) => handleDateFromChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={dateTo}
                onChange={(e) => handleDateToChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
              />
              <Button
                variant="outlined"
                startIcon={<RefreshCw size={18} />}
                onClick={() => {
                  setSearchQuery("");
                  setFilterStatus("all");
                  setFilterPayment("all");
                  setDateFrom("");
                  setDateTo("");
                  applyFilters(invoices, "", "all", "all", "", "");
                }}
                sx={{ borderRadius: 3 }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={selectedInvoices.length === 0}
                onClick={() => {
                  setDeleteDialogOpen(true);
                  setDeleteType("multiple");
                }}
                startIcon={<Trash2 size={18} />}
                sx={{ borderRadius: 3 }}
              >
                Delete ({selectedInvoices.length})
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: "white",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer sx={{ maxHeight: isMobile ? "60vh" : "65vh" }}>
            <Table stickyHeader size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      backgroundColor: "primary.light",
                      borderTopLeftRadius: "12px",
                    }}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      indeterminate={
                        selectedInvoices.length > 0 && !isAllSelected
                      }
                      size="small"
                      sx={{ color: "primary.main" }}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    <TableSortLabel
                      active={sortConfig.key === "id"}
                      direction={
                        sortConfig.key === "id" ? sortConfig.direction : "asc"
                      }
                      onClick={() => handleSort("id")}
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      Order ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    <TableSortLabel
                      active={sortConfig.key === "name"}
                      direction={
                        sortConfig.key === "name" ? sortConfig.direction : "asc"
                      }
                      onClick={() => handleSort("name")}
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    <TableSortLabel
                      active={sortConfig.key === "restaurantName"}
                      direction={
                        sortConfig.key === "restaurantName"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("restaurantName")}
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      Restaurant
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    <TableSortLabel
                      active={sortConfig.key === "totalPrice"}
                      direction={
                        sortConfig.key === "totalPrice"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("totalPrice")}
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    <TableSortLabel
                      active={sortConfig.key === "createdAt"}
                      direction={
                        sortConfig.key === "createdAt"
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort("createdAt")}
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "primary.light" }}>
                    Payment
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "primary.light",
                      borderTopRightRadius: "12px",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getCurrentPageData().map((invoice) => (
                  <StyledTableRow
                    key={invoice.id}
                    hover
                    className={selectedInvoices.includes(invoice.id) ? "selected" : ""}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedInvoices.includes(invoice.id)}
                        onChange={() => toggleSelectInvoice(invoice.id)}
                        size="small"
                        sx={{ color: "primary.main" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        #{invoice.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.light" }}>
                          <User size={16} color="#C4161C" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {users[invoice.userId]?.name || invoice.name}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Store size={16} color="#6B7280" />
                        <Typography variant="body2">
                          {users[invoice.userId]?.restaurantName || invoice.restaurantName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        £{invoice.totalPrice?.toFixed(2) || "0.00"}
                      </Typography>
                      {invoice.discountType && invoice.discountType !== 'none' && invoice.discountValue && parseFloat(invoice.discountValue) > 0 && (
                        <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic', display: 'block' }}>
                          (Includes Discount)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Calendar size={14} color="#6B7280" />
                        <Typography variant="body2">
                          {new Date(invoice.createdAt).toLocaleDateString()}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(invoice.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {editingInvoiceId === invoice.id ? (
                        <Select
                          value={orderStatusValue || invoice.orderStatus}
                          onChange={(e) =>
                            handleOrderStatusChange(invoice.id, e.target.value)
                          }
                          onBlur={() => setEditingInvoiceId(null)}
                          size="small"
                          sx={{ minWidth: 120, borderRadius: 2 }}
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="accepted">Accepted</MenuItem>
                          <MenuItem value="shipped">Shipped</MenuItem>
                          <MenuItem value="delivered">Delivered</MenuItem>
                        </Select>
                      ) : (
                        <Tooltip title="Click to edit">
                          <Box onClick={() => setEditingInvoiceId(invoice.id)}>
                            <StatusChip
                              label={invoice.orderStatus}
                              status={invoice.orderStatus}
                              icon={getStatusIcon(invoice.orderStatus)}
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {editingInvoiceId === invoice.id ? (
                        <Select
                          value={
                            paymentStatusValue ||
                            (invoice.isBillPaid ? "paid" : "pending")
                          }
                          onChange={(e) =>
                            handlePaymentStatusChange(
                              invoice.id,
                              e.target.value === "paid"
                            )
                          }
                          onBlur={() => setEditingInvoiceId(null)}
                          size="small"
                          sx={{ minWidth: 100, borderRadius: 2 }}
                        >
                          <MenuItem value="paid">Paid</MenuItem>
                          <MenuItem value="pending">Pending</MenuItem>
                        </Select>
                      ) : (
                        <Tooltip title="Click to edit">
                          <Box onClick={() => setEditingInvoiceId(invoice.id)}>
                            <PaymentChip
                              label={invoice.isBillPaid ? "Paid" : "Pending"}
                              paid={invoice.isBillPaid}
                              icon={getPaymentIcon(invoice.isBillPaid)}
                            />
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setIsModalOpen(true);
                            }}
                            sx={{
                              bgcolor: "primary.light",
                              "&:hover": { bgcolor: "primary.main", color: "white" },
                            }}
                          >
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send Email">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEmailDialog(invoice)}
                            sx={{
                              bgcolor: "info.light",
                              "&:hover": { bgcolor: "info.main", color: "white" },
                            }}
                          >
                            <Mail size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDeleteDialogOpen(true);
                              setDeleteType("single");
                              setInvoiceToDelete(invoice.id);
                            }}
                            sx={{
                              bgcolor: "error.light",
                              "&:hover": { bgcolor: "error.main", color: "white" },
                            }}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredInvoices.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "14px",
              },
            }}
          />
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "text.primary" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteType === "multiple" ? "the selected invoices" : "this invoice"}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Modal */}
      {selectedInvoice && (
        <InvoiceModal
          invoice={{
            ...selectedInvoice,
            restaurantName: users[selectedInvoice.userId]?.restaurantName || selectedInvoice.restaurantName,
            name: users[selectedInvoice.userId]?.name || selectedInvoice.name,
            address: users[selectedInvoice.userId]?.address || selectedInvoice.address,
            email: users[selectedInvoice.userId]?.email || selectedInvoice.restaurantEmail
          }}
          handleClose={() => {
            setSelectedInvoice(null);
            setIsModalOpen(false);
          }}
          isModalOpen={isModalOpen}
        />
      )}

      {/* Email Confirmation Dialog */}
      <Dialog
        open={emailDialog.open}
        onClose={() => !emailSending && setEmailDialog({ open: false, email: '', invoice: null })}
        PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 400 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          📧 Send Invoice via Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Invoice for <strong>{emailDialog.invoice?.name || 'Customer'}</strong> on{' '}
            {emailDialog.invoice ? new Date(emailDialog.invoice.createdAt).toLocaleDateString('en-GB') : ''}
          </Typography>
          <TextField
            fullWidth
            label="Recipient Email"
            type="email"
            value={emailDialog.email}
            onChange={(e) => setEmailDialog(prev => ({ ...prev, email: e.target.value }))}
            sx={{ mb: 1 }}
            helperText="You can change the email address before sending"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEmailDialog({ open: false, email: '', invoice: null })}
            disabled={emailSending}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={emailSending || !emailDialog.email}
            sx={{ borderRadius: 2 }}
          >
            {emailSending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                Sending...
              </Box>
            ) : (
              'Send Email'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceScreen;